/**
 * Las reglas de validación del formulario de registro. HU-01 a HU-08.
 *
 * Viven aquí y no dentro de la pantalla por dos razones:
 *
 *   1. **Se pueden probar.** Dentro de un `useMemo` había que montar ocho secciones
 *      de JSX para comprobar una regla; el resultado fue que no había ni una prueba
 *      sobre ellas, y la auditoría del plan de QA encontró tres reglas ausentes.
 *   2. **Se leen juntas.** Son el contrato de lo que el formulario exige, y contrastarlo
 *      con `faltantes_para_enviar()` del backend —que es la autoridad— exige poder
 *      verlas de un vistazo. Las dos listas ya divergieron una vez: el servidor pedía
 *      evidencias y causalidad, y el formulario no las mencionaba.
 *
 * Son reglas de **ENVÍO**, no de guardado. El borrador se guarda siempre, incompleto y
 * todo: HU-36 lo exige y HU-01 lo repite. Nada de aquí impide persistir.
 */

// CP-02-11: "teléfono chileno con prefijo internacional se acepta sin forzar el
// formato peruano". Con 14 mercados, imponer un formato nacional rechazaría números
// legítimos — y el teléfono del notificante es como se le vuelve a contactar si el
// Comité necesita ampliar el caso. Se rechaza solo lo que NO puede ser un teléfono.
import type {
  AnimalDelFormulario, CasoDelFormulario, ConfiguracionDeCorreos, Faltante,
} from '../tipos';
import { fechaDeHoyEnZona } from './fechas';

/**
 * Un faltante con su grupo. `grupo` sirve para que el resumen junte en una sola linea
 * los que se repiten por fila ("3 animales sin especie") en vez de listar tres veces
 * lo mismo.
 */
type FaltanteConGrupo = Faltante & { grupo?: string };

/** La funcion que cada bloque de reglas usa para anotar un faltante. */
type Anotar = (campo: string, seccion: string, mensaje: string, grupo?: string) => void;

export const telefonoValido = (valor: unknown) => {
  const limpio = String(valor ?? '').replaceAll(/[\s()\-.]/g, '');
  return /^\+?\d{6,15}$/.test(limpio);
};

// Los que el requisito nombra uno a uno. Como LISTA y no como clase de caracteres de
// una expresión regular: ahí hay que escapar el corchete y la barra invertida a la vez,
// y es justo donde este tipo de filtro se escribe mal sin que nadie lo note — la clase
// queda mal cerrada y deja de filtrar, o filtra de más.
const CARACTERES_PROHIBIDOS = ['"', '(', ')', ':', ';', '<', '>', '[', ']', '\\'];

/**
 * El correo, campo a campo. Devuelve `{ error, sugerencia }` — las dos opcionales.
 *
 * POR QUE NO UNA SOLA EXPRESION REGULAR
 * --------------------------------------
 * La había: `/^[^@\s]+@[^@\s]+\.[^@\s]+$/`. Aceptaba `a@b.c` y `.pepe@mail..com`, y
 * sobre todo solo sabía decir "no tiene formato válido", que no le dice al usuario QUÉ
 * arreglar. Con reglas separadas, cada fallo trae su frase: la diferencia entre
 * corregirlo a la primera y probar a ciegas.
 *
 * `desechables` y `frecuentes` llegan de `parametro` (HU-31), no escritos aquí: los
 * dominios temporales aparecen y desaparecen, y una lista en el código envejece sin que
 * nadie lo note. Si no llegan, esas dos comprobaciones simplemente no se hacen.
 */
export function revisarCorreo(valor: unknown, configuracion: ConfiguracionDeCorreos = {}) {
  const correo = normalizarCorreo(valor);
  if (!correo) return {};

  // Las tres tandas van en ESTE orden y no en otro: cada una explica el fallo mejor
  // que la siguiente. Un espacio contado como "estructura inválida" manda al usuario
  // a mirar la arroba, que está perfectamente puesta.
  const error = caracterQueNoPuedeIr(correo)
    || problemaDeEstructura(correo)
    || problemaDelDominio(dominioDe(correo), configuracion.desechables ?? []);
  if (error) return { error };

  // SUGERENCIA, NO ERROR. Un dominio corporativo legítimo puede parecerse a uno
  // frecuente, así que esto nunca bloquea el envío: solo pregunta.
  const parecido = dominioParecido(dominioDe(correo), configuracion.frecuentes ?? []);
  if (parecido) return { sugerencia: `¿Quisiste decir @${parecido}?` };

  return {};
}

/** La mitad de después de la arroba. Solo se llama con un correo ya estructurado. */
const dominioDe = (correo: string): string => correo.split('@')[1] ?? '';

/**
 * Caracteres que no pueden estar en NINGUNA parte de una dirección, o `null`.
 *
 * Se comprueban antes que la estructura porque un espacio o una coma explican el
 * fallo mejor que "estructura inválida", que es lo que diría el resto de reglas al
 * tropezar con ellos.
 */
function caracterQueNoPuedeIr(correo: string): string | null {
  if (/\s/.test(correo)) return 'El correo no puede llevar espacios';
  if (/,/.test(correo)) return 'El correo lleva una coma donde debería ir un punto';
  if (/[^\x20-\x7E]/.test(correo)) {
    return 'El correo no puede llevar tildes, eñes ni caracteres invisibles';
  }
  if (CARACTERES_PROHIBIDOS.some((caracter) => correo.includes(caracter))) {
    return 'El correo lleva un carácter que no se admite (" ( ) : ; < > [ ])';
  }
  return null;
}

/** Arroba, mitades y puntos mal puestos. Devuelve el mensaje o `null`. */
function problemaDeEstructura(correo: string): string | null {
  const partes = correo.split('@');
  if (partes.length < 2) return 'Al correo le falta la arroba';
  if (partes.length > 2) return 'El correo tiene más de una arroba';

  const [usuario, dominio] = partes;
  if (!usuario) return 'Falta el nombre de usuario antes de la arroba';
  if (!dominio) return 'Falta el dominio después de la arroba';
  if (!dominio.includes('.')) return 'Al dominio le falta el punto (ej. gmail.com)';

  // Puntos: ni al principio, ni al final, ni dos seguidos. En ninguna de las dos mitades.
  //
  // Los dos grupos no son decorativos. Sin ellos —`/^\.|\.$/`— hay que saberse de
  // memoria que la alternancia `|` tiene MENOS precedencia que la concatenacion para
  // leer que el `^` ancla solo al primer lado y el `$` solo al segundo. Se lee igual
  // de bien como "empieza en punto" O "termina en punto", que es lo que dice el
  // comentario de arriba, y el analisis de calidad deja de marcarlo (javascript:S5850).
  // Comportamiento identico: verificado sobre 12 casos, incluidos '.', '..' y ''.
  if (/(?:^\.)|(?:\.$)/.test(usuario) || /(?:^\.)|(?:\.$)/.test(dominio)) {
    return 'El correo no puede empezar ni terminar en punto';
  }
  if (correo.includes('..')) return 'El correo no puede llevar dos puntos seguidos';
  return null;
}

/**
 * La terminación del dominio y la lista de desechables. Mensaje o `null`.
 *
 * El PROVEEDOR —lo que va entre la arroba y el último punto— no necesita regla propia:
 * que esté vacío implica un punto pegado a la arroba (`ana@.pe`) o dos puntos seguidos
 * (`ana@sub..pe`), y las dos cosas ya se rechazaron antes con un mensaje más concreto.
 * Se comprobó y se quitó: una rama que no puede ejecutarse nunca aparenta cubrir un
 * caso que en realidad cubre otra.
 */
function problemaDelDominio(dominio: string, desechables?: string[]): string | null {
  const tld = dominio.split('.').at(-1) ?? '';
  if (tld.length < 2) {
    return 'La terminación del dominio necesita al menos dos letras (.com, .pe)';
  }
  if (!/^[a-z]+$/.test(tld)) {
    return 'La terminación del dominio solo lleva letras (.com, .org, .pe)';
  }
  if ((desechables ?? []).includes(dominio)) {
    return `${dominio} es un correo temporal y no se acepta: es como el Comité vuelve a contactar al notificante`;
  }
  return null;
}

/** Trim + minúsculas. Sin esto, `Ana@Mail.com` y `ana@mail.com` son dos filas distintas. */
export const normalizarCorreo = (valor: unknown): string => String(valor ?? '').trim().toLowerCase();

/**
 * CP-05-09 · la fecha de inicio del evento no puede estar en el futuro.
 *
 * SUGERENCIA, NO ERROR, y es deliberado. HU-05 pide esta fecha "al enviar" y no declara
 * ninguna regla sobre fechas futuras; el caso de prueba dice «ADVIERTE al salir del
 * campo», no «impide». Bloquear iria mas alla de lo que la historia define y dejaria
 * atrapados los borradores que ya tienen una fecha futura guardada.
 *
 * Va por el mismo canal que las sugerencias de `revisarCorreo`: se pinta como ayuda
 * bajo el campo y no entra en la lista de datos faltantes, asi que no toca el envio.
 *
 * HOY NO AVISA: el evento pudo empezar esta manana. Solo a partir de manana. Se compara
 * con el HOY DEL PAIS DEL CASO (RN-82, Bug #1086), no con el del navegador: cerca de la
 * medianoche el de Lima y el de Mexico o Chile son dias distintos. Y sobre el texto
 * `AAAA-MM-DD` que da el campo de fecha; construir un `Date` con ese texto lo
 * interpretaria en UTC y en Peru adelantaria el corte cinco horas.
 */
export function revisarFechaDeInicio(valor: unknown, zonaHoraria: string | null | undefined, ahora: Date = new Date()) {
  const fecha = String(valor ?? '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return {};

  if (fecha <= fechaDeHoyEnZona(zonaHoraria, ahora)) return {};

  return { sugerencia: 'Esa fecha aún no ha llegado: revisa que el evento haya ocurrido.' };
}

/**
 * El dominio frecuente que está a UNA errata de distancia, o `null`.
 *
 * Distancia 1 o 2 según el largo: con umbral fijo de 2, dominios cortos y legítimos
 * ("une.pe" frente a "one.pe") se sugerirían entre sí constantemente, y una sugerencia
 * que se equivoca a menudo se ignora también cuando acierta.
 */
export function dominioParecido(dominio: string, frecuentes?: string[]): string | null {
  // Sin lista de frecuentes no hay con que comparar: no se sugiere nada, que es
  // exactamente lo que hacia antes cuando la configuracion no estaba disponible.
  const lista = frecuentes ?? [];
  if (lista.includes(dominio)) return null;
  const umbral = dominio.length <= 8 ? 1 : 2;
  let mejor: string | null = null;
  let menor = Infinity;
  for (const candidato of lista) {
    const d = distancia(dominio, candidato);
    if (d > 0 && d <= umbral && d < menor) {
      menor = d;
      mejor = candidato;
    }
  }
  return mejor;
}

/** Levenshtein, con una sola fila de trabajo: son cadenas de 10-15 caracteres. */
function distancia(a: string, b: string): number {
  if (a === b) return 0;
  let fila = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const siguiente = [i];
    for (let j = 1; j <= b.length; j += 1) {
      siguiente[j] = Math.min(
        fila[j] + 1,
        siguiente[j - 1] + 1,
        fila[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    fila = siguiente;
  }
  return fila[b.length];
}

/**
 * La lista canónica de faltantes, CAMPO A CAMPO: `{ campo, seccion, mensaje, grupo }`.
 *
 * El `campo` es lo que hace posibles las dos cosas que HU-01 pide y que hasta ahora no
 * existían: pintar el error junto al control que lo causa —CP-02-10, "error junto al
 * campo, sin bloquear el borrador"— y que cada línea del resumen lleve el foco a ese
 * control, no solo a su sección.
 *
 * `grupo` marca los faltantes que se repiten por fila (los medicamentos): junto al campo
 * se muestran uno a uno, y en el resumen se cuentan en una sola línea.
 *
 * `configuracion` trae las listas de dominios de `parametro` (HU-31). Es opcional: sin
 * ella el correo se valida igual en estructura, y solo se dejan de comprobar las dos
 * reglas que dependen de una lista —desechables y sugerencia de tipeo—.
 */
export function evaluarFormulario(
  c: CasoDelFormulario,
  afectaPersonas: boolean,
  configuracion: ConfiguracionDeCorreos = {},
): FaltanteConGrupo[] {
  const f: FaltanteConGrupo[] = [];
  const add: Anotar = (campo, seccion, mensaje, grupo) => { f.push({ campo, seccion, mensaje, grupo }); };

  // Una función por bloque de secciones, y en el ORDEN en que se evalúan: el resumen
  // de faltantes se lee en este orden y las pruebas lo fijan. Están separadas porque
  // la lista entera no cabía de un vistazo — que es justo lo que esta función tiene
  // que permitir: contrastarla con `faltantes_para_enviar()` del backend.
  faltantesDelEncabezado(c, add);
  faltantesDelNotificanteYCliente(c, add, configuracion);
  faltantesDeCategorias(c, add);
  faltantesDeAnimales(c, add);
  faltantesDeMedicamentos(c, add);
  faltantesDelEvento(c, add);

  if (afectaPersonas && !c.personas?.vias?.length) add('personas.vias', 's7', 'Indica la vía de exposición de la persona');
  if (afectaPersonas) faltanteDeLaEdadDeLaPersona(c, add);
  if (afectaPersonas) faltantesDelDetalleDeOtros(c, add);

  if (!c.declaracion.trim()) add('declaracion', 's8', 'Falta el nombre del responsable del llenado');

  return f;
}

/**
 * Sección 7. La edad de la persona afectada va de 18 a 120 años (CP-06-12, 3-sep-2026).
 *
 * El campo sigue siendo OPCIONAL: en blanco no se reclama nada. Lo que se rechaza es una
 * edad escrita que caiga fuera del rango, para que el registrador lo vea aquí y no al
 * pulsar enviar —el servidor la exige igual, en el objeto de valor y en la base—.
 *
 * El mínimo no es plausibilidad clínica sino regla de captura: el bloque es dato L2 y su
 * consentimiento lo firma la propia persona afectada. Decisión del operador, con la
 * consecuencia asumida de que un menor no se puede registrar con su edad real.
 */
export const EDAD_MINIMA_DE_LA_PERSONA = 18;
export const EDAD_MAXIMA_DE_LA_PERSONA = 120;

function faltanteDeLaEdadDeLaPersona(c: CasoDelFormulario, add: Anotar) {
  const escrita = String(c.personas?.edad ?? '').trim();
  if (!escrita) return;

  const edad = Number(escrita);
  if (Number.isInteger(edad) && edad >= EDAD_MINIMA_DE_LA_PERSONA && edad <= EDAD_MAXIMA_DE_LA_PERSONA) return;
  add('personas.edad', 's7',
    `La edad de la persona afectada va de ${EDAD_MINIMA_DE_LA_PERSONA} a ${EDAD_MAXIMA_DE_LA_PERSONA} años`);
}

/**
 * Sección 7. Marcar «Otros» obliga a decir cuál fue (CP-06-13, 3-sep-2026).
 *
 * En la vía de exposición lo pide HU-06 —su fila es "Otros (especificar)"—; en el sitio
 * de la inyección lo decidió el operador por el mismo motivo. Una opción marcada como
 * «Otro» sin decir cuál no informa de nada, y el Comité la necesita para la causalidad.
 *
 * Solo se reclama con la casilla marcada: desmarcarla quita la exigencia y el texto ni
 * siquiera viaja al servidor.
 */
function faltantesDelDetalleDeOtros(c: CasoDelFormulario, add: Anotar) {
  const p = c.personas;
  if (!p) return;

  if ((p.vias || []).includes('otros') && !String(p.viaOtrosDetalle ?? '').trim()) {
    add('personas.viaOtrosDetalle', 's7', 'Indica cuál fue esa otra vía de exposición');
  }
  if (p.sitioInyeccion === 'otro' && !String(p.sitioOtroDetalle ?? '').trim()) {
    add('personas.sitioOtroDetalle', 's7', 'Indica cuál fue ese otro sitio de la inyección');
  }
}

/** Sección 1. País y fecha de recepción: el mínimo para abrir el caso (HU-01). */
function faltantesDelEncabezado(c: CasoDelFormulario, add: Anotar) {
  if (!c.pais) add('pais', 's1', 'Falta el país');
  if (!c.fechaRecepcion) add('fechaRecepcion', 's1', 'Falta la fecha de recepción');
}

/** Sección 2. Quién notifica y quién es el dueño: nombre, correos y teléfonos. */
function faltantesDelNotificanteYCliente(c: CasoDelFormulario, add: Anotar, configuracion: ConfiguracionDeCorreos) {
  if (!c.notificante.nombre.trim()) add('notificante.nombre', 's2', 'El nombre del notificante es obligatorio');
  // El mensaje lo trae `revisarCorreo`, que dice QUÉ está mal ("le falta la arroba",
  // "lleva dos puntos seguidos") en vez del "no tiene formato válido" de antes, que
  // obligaba al usuario a adivinar. La sugerencia de tipeo NO es un faltante: no impide
  // enviar y se muestra aparte, junto al campo.
  const correoNotificante = revisarCorreo(c.notificante.email, configuracion);
  if (correoNotificante.error) add('notificante.email', 's2', correoNotificante.error);
  const correoCliente = revisarCorreo(c.cliente.email, configuracion);
  if (correoCliente.error) add('cliente.email', 's2', correoCliente.error);
  // CP-02-10/11/12. NO se fuerza el formato peruano: el alcance son 14 paises y el
  // caso CP-02-11 lo dice explicito con un telefono chileno con prefijo. Se acepta
  // cualquier numero internacional razonable y solo se rechaza lo que no puede ser
  // un telefono — letras, o menos de seis digitos.
  if (c.notificante.telefono && !telefonoValido(c.notificante.telefono)) add('notificante.telefono', 's2', 'El teléfono del notificante no parece un número válido');
  if (c.cliente.telefono && !telefonoValido(c.cliente.telefono)) add('cliente.telefono', 's2', 'El teléfono del cliente no parece un número válido');
}

/** Sección 3. Sin categoría, para el dominio el evento adverso no existe. */
function faltantesDeCategorias(c: CasoDelFormulario, add: Anotar) {
  if (!c.categorias.length) add('categorias', 's3', 'Marca al menos una categoría del evento');
}

/**
 * Sección 4. Un animal como mínimo, y cada fila con su especie.
 *
 * Mismo patrón que los medicamentos: el faltante se identifica POR FILA para que el
 * resumen pueda llevar el foco al animal concreto, y se agrupa en una sola línea al
 * resumirlo.
 */
function faltantesDeAnimales(c: CasoDelFormulario, add: Anotar) {
  if (!c.animales.length) add('animales', 's4', 'Registra al menos un animal afectado');
  c.animales.forEach((a, i) => {
    if (!a.especie) add(`animales.${i}.especie`, 's4', `Elige la especie del animal ${i + 1}`, 'animales.especie');
    faltantesDeCoherenciaDeConteosDelAnimal(a, i, add);
    faltanteDeLaEdadDelAnimal(a, i, add);
    faltanteDelPesoDelAnimal(a, i, add);
    faltanteDeLosTratadosDelAnimal(a, i, add);
  });
}

/**
 * La edad del animal se cuenta desde 1 (decisión del operador, 6-sep-2026).
 *
 * El campo aceptaba `0`, y un `0` en un campo de años no dice "recién nacido": dice que
 * alguien pulsó una tecla y siguió. En el Formato Verde, y en la señal de seguridad que
 * el Comité mira después, un cero se lee como dato y no como hueco.
 *
 * NO se vuelve obligatorio: HU-03 lo declara opcional y así sigue. En blanco es una
 * respuesta legítima —"no se sabe"— y no se reclama. Lo que se rechaza es el cero escrito.
 *
 * ⚠ CONSECUENCIA ASUMIDA, y hay que decirla: un animal de MENOS de un año —un ternero de
 * tres meses, un cachorro— no tiene edad expresable en años enteros. Con esta regla su
 * edad se deja EN BLANCO, porque el campo es de años completos (HU-03: "solo dígitos, sin
 * decimales, en años"). Si el negocio necesita registrar neonatos con precisión, el
 * arreglo no es permitir el cero sino cambiar la unidad del campo, y eso es otra decisión.
 */
function faltanteDeLaEdadDelAnimal(a: AnimalDelFormulario, i: number, add: Anotar) {
  const escrita = String(a?.edad ?? '').trim();
  if (!escrita) return;                       // opcional: en blanco no se reclama
  if (Number(escrita) >= 1) return;
  add(`animales.${i}.edad`, 's4',
    `Animal ${i + 1}: la edad va en años completos, desde 1. Si es menor de un año, déjala en blanco`,
    'animales.edad');
}

/**
 * El peso del animal es mayor que 0 (decisión del operador, 7-sep-2026).
 *
 * Un animal de cero kilos no existe. El cero aquí es lo que deja un campo por el que
 * alguien pasó sin saber el dato, y en el Formato Verde se lee como una medición.
 *
 * NO se vuelve obligatorio: en blanco significa «no se pesó» y es respuesta válida.
 *
 * ⚠ ADMITE DECIMALES, y por eso NO se compara contra 1 como la edad: `0.4` es un peso
 * perfectamente válido —un pollo, un cachorro, un lechón— y rechazarlo dejaría fuera a
 * las especies pequeñas, que son justo las que se pesan en fracciones. Lo único que se
 * rechaza es el cero: `0`, `0.0`, `00`.
 *
 * El campo ya normaliza la coma a punto (`pesoDecimal`), así que aquí llega con punto.
 */
function faltanteDelPesoDelAnimal(a: AnimalDelFormulario, i: number, add: Anotar) {
  const escrito = String(a?.peso ?? '').trim();
  if (!escrito) return;                       // opcional: en blanco no se reclama
  if (Number(escrito) > 0) return;
  add(`animales.${i}.peso`, 's4',
    `Animal ${i + 1}: el peso tiene que ser mayor que 0. Si no se pesó, déjalo en blanco`,
    'animales.peso');
}

/**
 * El N.º tratados del animal es mayor que 0 (decisión del operador, 7-sep-2026).
 *
 * Este bloque describe un grupo de animales TRATADOS: si no se trató ninguno, el grupo
 * no tiene por qué estar en el formulario. Un `0` aquí no informa, y además arrastra:
 * desde el 3-sep este campo alimenta el total de la sección 6, así que un cero se suma
 * como dato medido y hace que el total diga «se contaron 0» en vez de «no se contó».
 *
 * NO se vuelve obligatorio: en blanco sigue significando «no se sabe cuántos», y esos
 * bloques quedan fuera de la suma sin inventar un cero.
 */
function faltanteDeLosTratadosDelAnimal(a: AnimalDelFormulario, i: number, add: Anotar) {
  const escrito = String(a?.tratados ?? '').trim();
  if (!escrito) return;                       // opcional: en blanco no se reclama
  if (Number(escrito) >= 1) return;
  add(`animales.${i}.tratados`, 's4',
    `Animal ${i + 1}: el número de tratados tiene que ser mayor que 0. Si no se sabe, déjalo en blanco`,
    'animales.tratados');
}

/**
 * Tratados ≥ con signos ≥ muertos, POR ANIMAL (HU-03, 2-sep-2026).
 *
 * Misma regla que `faltantesDeCoherenciaDeConteos` para los totales del evento, y el
 * mismo criterio: basta con dos de los tres para comprobarla, y el faltante se cuelga
 * del campo MAYOR de la pareja, que es el que hay que corregir. El servidor la exige
 * igual (objeto de valor del animal), así que avisar aquí evita un 422 al guardar.
 */
function faltantesDeCoherenciaDeConteosDelAnimal(a: AnimalDelFormulario, i: number, add: Anotar) {
  const t = +a.tratados || 0, s = +a.conSignos || 0, m = +a.muertos || 0;
  const n = i + 1;
  if (a.conSignos && a.muertos && m > s) add(`animales.${i}.muertos`, 's4', `Animal ${n}: no puede haber más muertos que animales con signos`);
  else if (a.tratados && a.muertos && m > t) add(`animales.${i}.muertos`, 's4', `Animal ${n}: no puede haber más muertos que animales tratados`);
  if (a.tratados && a.conSignos && s > t) add(`animales.${i}.conSignos`, 's4', `Animal ${n}: no puede haber más animales con signos que tratados`);
}

/**
 * Sección 5. Un faltante POR MEDICAMENTO, no uno por sección.
 *
 * Con tres medicamentos en pantalla, "falta el producto" sin decir en cuál obliga a
 * revisarlos de uno en uno. El resumen los vuelve a juntar en una sola línea (ver
 * `RESUMEN_DE_GRUPO`).
 */
function faltantesDeMedicamentos(c: CasoDelFormulario, add: Anotar) {
  if (!c.medicamentos.length) add('medicamentos', 's5', 'Registra al menos un medicamento sospechoso');
  c.medicamentos.forEach((m, i) => {
    if (!m.sku) add(`medicamentos.${i}.sku`, 's5', `Elige el producto del medicamento ${i + 1}`, 'medicamentos.sku');
    // CP-01-09 / CP-08-02. El SERVIDOR exige esta respuesta para enviar, y hasta ahora
    // el formulario no la pedia: el registrador llenaba las ocho secciones, veia todo
    // en verde, pulsaba enviar y ahi descubria que le faltaba. Es justo el fallo que
    // HU-01 existe para evitar — "me dice cuantos DATOS faltan", no despues.
    if (!m.esCausa) add(`medicamentos.${i}.esCausa`, 's5', 'Indica si crees que el efecto adverso se debe al medicamento', 'medicamentos.esCausa');
  });
  // ⚠ NO se exige que un «Sí» tenga otro medicamento detrás, y es deliberado.
  //
  // Se llegó a implementar como faltante de envío el 3-sep-2026 y se retiró el mismo
  // día: HU-04 no lo pide en ninguna parte, y el AF dice lo contrario con todas las
  // letras — «un "Sí" con uno solo capturado significa que hubo otros SIN DOCUMENTAR,
  // y eso el Comité necesita verlo». Bloquear el envío empuja al notificante a
  // responder «No» o a inventar un producto, y las dos cosas destruyen justo la señal
  // que la pregunta existe para capturar.
}

/** Sección 6. Descripción del evento, coherencia de los conteos y evidencias. */
function faltantesDelEvento(c: CasoDelFormulario, add: Anotar) {
  if (!c.evento.detalle.trim()) add('evento.detalle', 's6', 'Describe el evento adverso');
  // El servidor la exige dentro del bloque del evento (`EventoAdversoEntrada`), asi que
  // sin ella el caso no se puede enviar. Decirlo AQUI y no al pulsar enviar (CP-05-03).
  if (!c.evento.inicio) add('evento.inicio', 's6', 'Indica la fecha de inicio del evento');
  faltantesDeCoherenciaDeConteos(c, add);
  faltantesDeLosTiemposDelEvento(c, add);
  // No hay regla de fecha de término: el Formato Verde no la captura. La pide el Formato
  // Amarillo (§8) y se le solicita al analista al generar la notificación (AF §7c).

  // CP-07-43/44 y el control C-01: "toda sospecha esta registrada CON EVIDENCIAS
  // ADJUNTAS". Es obligatorio por norma y el servidor lo bloquea; el formulario
  // tiene que decirlo ANTES, no dejar que se descubra al pulsar enviar.
  // Las evidencias se adjuntan DENTRO de la seccion 6, asi que el faltante se
  // cuelga de 's6'. Con una clave propia el enlace del resumen —CP-01-08, "cada
  // dato faltante navega a su campo"— no llevaria a ninguna parte.
  if (!c.evidencias.length) add('evidencias', 's6', 'Adjunta al menos una evidencia: el caso no se puede enviar sin sustento');
}

/**
 * Los dos tiempos del evento se cuentan desde 1 (decisión del operador, 7-sep-2026).
 *
 * «Tiempo entre administración y aparición» y «Duración del efecto adverso» admitían `0`.
 * Un cero aquí no informa de nada: un efecto que dura cero no es un efecto, y una
 * aparición a las cero horas es la forma que tiene un formulario de decir «alguien pasó
 * por el campo». En el Formato Verde ese cero se lee como dato medido.
 *
 * NINGUNO SE VUELVE OBLIGATORIO. Los dos son opcionales hoy y lo siguen siendo: en
 * blanco es una respuesta legítima —no se sabe cuánto tardó, o el efecto sigue en
 * curso— y no se reclama. Lo único que se rechaza es el cero escrito.
 *
 * La unidad no se toca: quien necesite registrar algo menor que la unidad más pequeña
 * cambia de unidad —de días a horas, de horas a minutos—, que es justo para lo que
 * está el desplegable de al lado.
 */
function faltantesDeLosTiemposDelEvento(c: CasoDelFormulario, add: Anotar) {
  // El campo se estrecha a los DOS que la regla toca. `keyof EventoDelFormulario`
  // dejaria pasar `severidad` o `detalle`, que no son tiempos y no se comparan asi.
  const desdeUno = (campo: 'tiempoAparicion' | 'duracion', etiqueta: string) => {
    const escrito = String(c.evento?.[campo] ?? '').trim();
    if (!escrito) return;                       // opcional: en blanco no se reclama
    if (Number(escrito) >= 1) return;
    add(`evento.${campo}`, 's6', `${etiqueta} tiene que ser mayor que 0. Si no lo sabes, déjalo en blanco`);
  };
  desdeUno('tiempoAparicion', 'El tiempo entre la administración y la aparición');
  desdeUno('duracion', 'La duración del efecto adverso');
}

/**
 * Tratados ≥ con signos ≥ muertos.
 *
 * Se comprueba SIEMPRE que haya dos de los tres conteos: exigir `tratados` dejaba pasar
 * "9 muertos y 5 con signos" sin decir nada. El faltante se cuelga del campo MAYOR de la
 * pareja —el que hay que corregir—, no de los dos a la vez.
 */
function faltantesDeCoherenciaDeConteos(c: CasoDelFormulario, add: Anotar) {
  const t = +c.evento.tratados || 0, s = +c.evento.conSignos || 0, m = +c.evento.muertos || 0;
  if (c.evento.conSignos && c.evento.muertos && m > s) add('evento.muertos', 's6', 'Coherencia: no puede haber más muertos que animales con signos');
  if (c.evento.tratados && c.evento.conSignos && s > t) add('evento.conSignos', 's6', 'Coherencia: no puede haber más animales con signos que tratados');
}

/**
 * Cómo se cuenta en el RESUMEN un faltante que se repite por fila.
 *
 * Junto al campo conviene el mensaje concreto —el usuario ya está mirando ese
 * medicamento—; en el resumen conviene el agregado, porque diez medicamentos sin
 * responder producirían diez líneas idénticas que no distinguen nada.
 */
const RESUMEN_DE_GRUPO: Record<string, (n: number) => string> = {
  'animales.especie': (n: number) => (n === 1
    ? 'La especie es obligatoria'
    : `Elige la especie en ${n} animales`),
  'animales.edad': (n: number) => (n === 1
    ? 'La edad del animal va en años completos, desde 1'
    : `Corrige la edad en ${n} animales: va en años completos, desde 1`),
  'animales.peso': (n: number) => (n === 1
    ? 'El peso del animal tiene que ser mayor que 0'
    : `Corrige el peso en ${n} animales: tiene que ser mayor que 0`),
  'animales.tratados': (n: number) => (n === 1
    ? 'El N.º tratados del animal tiene que ser mayor que 0'
    : `Corrige el N.º tratados en ${n} animales: tiene que ser mayor que 0`),
  'medicamentos.sku': () => 'Cada medicamento debe tener un producto seleccionado',
  'medicamentos.esCausa': (n: number) => (n === 1
    ? 'Indica si crees que el efecto adverso se debe al medicamento'
    : `Indica en ${n} medicamentos si el efecto adverso se debe a ellos`),
};

/**
 * Devuelve `{ s1: [...], s2: [...] }` — los faltantes agrupados por sección.
 *
 * Agrupado y no como lista plana porque HU-01 pide dos cosas a la vez: contar DATOS
 * ("no cuántas secciones") y que cada uno lleve a su sección. La clave es la de la
 * sección donde el campo se rellena, que no siempre es la obvia: las evidencias se
 * adjuntan dentro de la 6.
 *
 * Se DERIVA de `evaluarFormulario`, nunca al revés: dos listas de reglas escritas por
 * separado divergen, y aquí ya pasó una vez entre el formulario y el servidor.
 */
export function reglasDelFormulario(
  c: CasoDelFormulario,
  afectaPersonas: boolean,
  configuracion: ConfiguracionDeCorreos = {},
) {
  const entradas = evaluarFormulario(c, afectaPersonas, configuracion);
  const cuantos: Record<string, number> = {};
  for (const x of entradas) if (x.grupo) cuantos[x.grupo] = (cuantos[x.grupo] || 0) + 1;

  const e: Record<string, string[]> = {};
  const vistos = new Set<string>();
  for (const x of entradas) {
    if (x.grupo) {
      if (vistos.has(x.grupo)) continue;
      vistos.add(x.grupo);
    }
    const mensaje = x.grupo ? RESUMEN_DE_GRUPO[x.grupo](cuantos[x.grupo]) : (x.mensaje ?? '');
    // La sección se crea la primera vez que aparece un faltante suyo. Antes esto era
    // `(e[x.seccion] = e[x.seccion] || []).push(...)`: asignar dentro de la expresión
    // que se usa esconde el efecto a la vista (S1121).
    e[x.seccion] ??= [];
    e[x.seccion].push(mensaje);
  }
  return e;
}


/**
 * Qué se perdería al conmutar una categoría, o `null` si no hay nada que perder.
 * CP-03-13/14 y CP-06-12/13/14.
 *
 * Conmutar "seguridad en personas" destruye datos **en un solo sentido**: al DESMARCARLA
 * se purga el bloque L2 entero por minimización (RNF-02).
 *
 * ⚠ EL OTRO SENTIDO DESAPARECIÓ EL 26-ago-2026, y conviene saber por qué. Marcarla
 * limpiaba raza y estado fisiológico, porque la sección 4 dejaba de describir al animal y
 * pasaba a describir a la persona (nota E26 del Formato Verde). Desde que las dos
 * coexisten —decisión del operador; el AF lo tenía como punto abierto derivado al PO— esa
 * sección sigue siendo la del animal y sus datos no se tocan. Marcar la categoría ya no
 * destruye nada, así que ya no hay nada que confirmar.
 *
 * Devuelve `null` cuando no hay nada escrito, y eso es deliberado: confirmar por
 * confirmar entrena a la gente a aceptar sin leer, y entonces la advertencia deja de
 * proteger justo el día que importa.
 */
export function datosEnRiesgoAlConmutar(caso: CasoDelFormulario, id: string, yaEstaba: boolean) {
  if (id !== 'seguridad_en_personas') return null;
  // MARCARLA no destruye nada: el animal se conserva entero.
  if (!yaEstaba) return null;

  const personas = caso.personas;
  const hayEnPantalla = personas && Object.entries(personas).some(([clave, valor]) =>
    clave === 'vias' ? Array.isArray(valor) && valor.length > 0 : Boolean(valor),
  );
  // O EN EL SERVIDOR, aunque esta pantalla no lo tenga (T-48b). Al retomar un borrador el
  // GET no devuelve la seccion 7 (RNF-03), asi que `personas` viene vacio y mirar solo la
  // pantalla concluia "no hay nada que perder" sobre un caso que SI tenia dato de salud
  // guardado. Como desmarcar la categoria ahora ordena la purga, esa conclusion equivocada
  // borraba el dato sin la confirmacion que HU-06 exige.
  const hay = hayEnPantalla || Boolean(caso.tieneDatoDePersonasEnServidor);
  return hay ? 'el bloque de la persona afectada' : null;
}
